import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
} from 'firebase/firestore'
import { db, COLLECTIONS } from '@/lib/firebase'
import { normalizePhone } from '@/lib/customerKeys'
import { deleteImage, uploadBugReportAttachments } from '@/services/storage'
import type { BugReport } from '@/types'

const col = collection(db, COLLECTIONS.bugReports)

export interface SubmitBugReportInput {
  fullName: string
  email: string
  phone: string
  description: string
  files: File[]
}

export async function submitBugReport(input: SubmitBugReportInput): Promise<string> {
  const docRef = doc(col)
  const id = docRef.id

  const phone = normalizePhone(input.phone)
  if (phone.length < 10) {
    throw new Error('Please enter a valid phone number.')
  }

  const payload: BugReport = {
    id,
    fullName: input.fullName.trim(),
    email: input.email.trim().toLowerCase(),
    phone,
    description: input.description.trim(),
    attachments: [],
    createdAt: new Date().toISOString(),
  }

  await setDoc(docRef, payload)

  if (input.files.length === 0) {
    return id
  }

  try {
    const attachments = await uploadBugReportAttachments(input.files, id)
    await updateDoc(docRef, { attachments })
  } catch (err) {
    await deleteDoc(docRef).catch(() => {})
    throw err
  }

  return id
}

export async function fetchBugReports(): Promise<BugReport[]> {
  const q = query(col, orderBy('createdAt', 'desc'))
  const snap = await getDocs(q)
  return snap.docs.map((d) => d.data() as BugReport)
}

export async function deleteBugReport(report: BugReport): Promise<void> {
  await Promise.all(
    report.attachments.map((a) => deleteImage(a.url).catch(() => {}))
  )
  await deleteDoc(doc(db, COLLECTIONS.bugReports, report.id))
}
